import { SpinTheWheelStore } from '@/store/SpinTheWheelStore';
import { SectionType } from '@/constants/SpinTheWheel/SectionType';

/**
 * Calculates the boosted coin amount based on active wheel rewards
 * @param originalCoins The original coin amount
 * @returns An object with original and boosted coin amounts, and whether a boost is active
 */
export const calculateBoostedCoins = (originalCoins: number): { 
  originalCoins: number; 
  boostedCoins: number; 
  hasActiveBoost: boolean;
} => {
  const spinTheWheelStore = SpinTheWheelStore();
  const allRewards = spinTheWheelStore.activeWheelRewards;
  
  // Filter out expired rewards
  const activeRewards = allRewards.filter(reward => {
    // If no end_time, consider it active
    if (!reward.end_time) return true;
    
    // If end_time exists, check if it's in the future
    // end_time is stored as a timestamp in seconds, need to convert to milliseconds
    const now = Math.floor(Date.now() / 1000); // Convert to seconds for comparison
    const isActive = reward.end_time > now;
    return isActive;
  });
  
  // If no active rewards or original coins is undefined, return original amount
  if (!activeRewards.length || originalCoins === undefined || originalCoins === null) {
    return {
      originalCoins: originalCoins || 0,
      boostedCoins: originalCoins || 0,
      hasActiveBoost: false
    };
  }

  // Find active percentage boosts
  const percentageBoosts = activeRewards.filter(
    reward => reward.type === SectionType.FIXED_PERCENTAGE || 
              reward.type === SectionType.TIME_GATED_PERCENTAGE
  );

  // If no percentage boosts, return original amount
  if (!percentageBoosts.length) {
    return {
      originalCoins,
      boostedCoins: originalCoins,
      hasActiveBoost: false
    };
  }

  // Apply percentage boosts sequentially in reverse order (last record first)
  let boostedCoins = originalCoins;
  
  // Reverse the array to apply the last record first
  const reversedBoosts = [...percentageBoosts].reverse();
  
  // Apply each boost sequentially
  reversedBoosts.forEach(boost => {
    const boostPercentage = boost.value;
    
    // Apply this individual boost to the current boosted value
    // Use Math.round instead of Math.floor to properly handle small boost values
    const additionalBoost = Math.round(boostedCoins * (boostPercentage / 100));
    boostedCoins += additionalBoost;
  });
  
  return {
    originalCoins,
    boostedCoins,
    hasActiveBoost: true,
  };
};
